import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment,
  QueryConstraint,
  documentId,
} from 'firebase/firestore';
import { chunkArray, FIRESTORE_IN_QUERY_LIMIT } from './batch-fetcher';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  updateEmail,
  updatePassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  User as FirebaseUser
} from 'firebase/auth';
import { db, auth } from './firebase';
import { User } from '../types/content';
import { RBACService } from './rbac-service';
import { Permission } from '../types/permissions';
import { revalidationService } from './revalidation-service';
import { generateAuthorSlug } from './utils';
import {
  handleUserError,
  handleCriticalError,
  handleSilentError,
  handleDataFetchError
} from './error-handler';
import { isValidRole, VALID_ROLES } from './auth-roles';

/**
 * Guard the user-management write seams (AH-2): a role write may only mint an assignable role.
 * Rejects garbage/injected/typo role strings ('superadmin', '', 'Owner ') before they reach
 * Firestore. Behavior-preserving for every real role (all enum values pass).
 */
function assertAssignableRole(role: unknown): void {
  if (typeof role !== 'string' || !isValidRole(role)) {
    throw new Error(
      `Invalid role: ${JSON.stringify(role)}. Assignable roles: ${VALID_ROLES.join(', ')}`,
    );
  }
}

/**
 * Guard owner-minting (AH-6 owner-mints-owner): ONLY a user whose own role is `owner` may
 * grant/MINT the `owner` role. Admins may assign every other role, but never mint owner.
 * Mirrors the firestore.rules `ownerGrantOk()` TRANSITION guard so the service layer
 * (Admin-SDK/trusted-UI path) and the rules agree exactly.
 *
 * A "mint" of owner = assigning role 'owner' to a target that was NOT already an owner:
 *   - createUser: a new doc has no prior role, so every owner-create IS a mint (omit priorRole).
 *   - updateUser: pass the target's current role. Promoting a non-owner → owner is a mint and is
 *     owner-only; EDITING or DEMOTING an existing owner (priorRole === 'owner') is NOT a mint and
 *     is allowed for admins — exactly as `ownerGrantOk()` allows (the EditUserModal re-sends the
 *     unchanged role:'owner' on every owner edit, which must not throw).
 */
export function assertOwnerMintAuthority(
  role: unknown,
  currentUser: Pick<User, 'role'>,
  priorRole?: unknown,
): void {
  const isMint = role === 'owner' && priorRole !== 'owner';
  if (isMint && currentUser.role !== 'owner') {
    throw new Error('Only an owner may grant the owner role.');
  }
}

export interface CreateUserData {
  email?: string; // Optional for Twitter users
  password?: string; // Optional for Twitter users
  displayName: string;
  role: 'contributor' | 'member' | 'writer' | 'editor' | 'admin' | 'owner';
  isActive?: boolean;
  // Twitter OAuth data (optional)
  authProvider?: 'twitter' | 'email' | 'system';
  twitterId?: string;
  twitterUsername?: string;
  twitterDisplayName?: string;
  twitterProfileImageUrl?: string;
}

export interface CreateGuestUserData {
  displayName: string;
  role?: 'writer'; // Guest users are always writers
  bio?: string;
  homepage?: string;
  twitterHandle?: string;
  profileImage?: string;
  isActive?: boolean;
}

export interface UpdateUserData {
  email?: string;
  displayName?: string;
  role?: 'contributor' | 'member' | 'writer' | 'editor' | 'admin' | 'owner';
  isActive?: boolean;
  bio?: string;
  homepage?: string;
  twitterHandle?: string; // DEPRECATED: Use twitterUsername instead
  twitterUsername?: string; // Verified Twitter handle
  twitterId?: string;
  twitterDisplayName?: string;
  twitterProfileImageUrl?: string;
  profileImage?: string;
  authProvider?: 'twitter' | 'email' | 'system';
  // Per-user permission grants (beyond role-based permissions)
  grantedPermissions?: string[];
}

export interface UserWithStats extends User {
  articleCount: number;
  lastActivityAt?: Date;
  totalUploads: number;
  // Explicitly include optional User fields commonly accessed in admin UI
  applicationStatus?: 'pending' | 'approved' | 'rejected';
  requestedTags?: string[];
  twitterId?: string;
  twitterUsername?: string;
}

/**
 * User Management Service - Admin Operations
 * 
 * Handles administrative user management:
 * - Create/delete users (admin only)
 * - Change user roles (admin/owner only)
 * - View all users with stats
 * - Batch user operations
 * - User activation/deactivation
 * 
 * For user's own profile operations (update profile, upload image),
 * see profile-service.ts
 * 
 * @example
 * // Get all users with stats (admin)
 * const users = await userManagementService.getAllUsers({}, currentUser);
 * 
 * // Create a new user (admin)
 * await userManagementService.createUser(userData, currentUser, adminCredentials);
 * 
 * // Get user by ID
 * const user = await userManagementService.getUserById(userId);
 */
export class UserManagementService {
  private static instance: UserManagementService;
  private collectionName = 'users';

  // User cache for batch fetching (5 minute TTL, matches tag-service pattern)
  private userCache = new Map<string, { user: User; timestamp: number }>();
  private readonly USER_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 500;

  static getInstance(): UserManagementService {
    if (!UserManagementService.instance) {
      UserManagementService.instance = new UserManagementService();
    }
    return UserManagementService.instance;
  }

  /**
   * Clean up old cache entries when cache grows too large
   */
  private cleanupCache(): void {
    if (this.userCache.size > this.MAX_CACHE_SIZE) {
      const entriesToRemove = this.userCache.size - this.MAX_CACHE_SIZE;
      const entries = Array.from(this.userCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      for (let i = 0; i < entriesToRemove; i++) {
        this.userCache.delete(entries[i][0]);
      }
    }
  }

  private getDb() {
    if (!db) {
      handleCriticalError(
        new Error('Database not initialized'),
        'UserManagementService.getDb'
      );
    }
    return db;
  }

  private getAuth() {
    if (!auth) {
      handleCriticalError(
        new Error('Auth not initialized'),
        'UserManagementService.getAuth'
      );
    }
    return auth;
  }

  /**
   * Get all users with optional filtering and sorting (with RBAC security)
   */
  async getAllUsers(
    filters: {
      role?: 'contributor' | 'member' | 'writer' | 'editor' | 'admin' | 'owner';
      isActive?: boolean;
      search?: string;
    } = {},
    currentUser: User
  ): Promise<UserWithStats[]> {
    // Check permissions
    await RBACService.authorize(
      currentUser,
      Permission.USER_READ_ALL,
      undefined,
      'You do not have permission to view all users'
    );
    try {
      const database = this.getDb();
      let q = collection(database, this.collectionName);
      const queryConstraints: QueryConstraint[] = [];

      // NOTE: We filter isDeleted client-side instead of in the query to avoid index requirements
      // This ensures backward compatibility with existing users that don't have the field yet

      // Apply filters
      if (filters.role) {
        queryConstraints.push(where('role', '==', filters.role));
      }

      if (filters.isActive !== undefined) {
        queryConstraints.push(where('isActive', '==', filters.isActive));
      }

      // Add ordering by creation date
      queryConstraints.push(orderBy('createdAt', 'desc'));

      const usersQuery = query(q, ...queryConstraints);
      const snapshot = await getDocs(usersQuery);

      const users: UserWithStats[] = [];

      for (const userDoc of snapshot.docs) {
        const userData = userDoc.data();

        // Client-side filter: Skip deleted users
        if (userData.isDeleted === true) {
          continue;
        }

        const user: UserWithStats = {
          id: userDoc.id,
          email: userData.email,
          displayName: userData.displayName,
          role: userData.role,
          isActive: userData.isActive,
          createdAt: userData.createdAt?.toDate() || new Date(),
          lastLoginAt: userData.lastLoginAt?.toDate(),
          profileImage: userData.profileImage,
          bio: userData.bio,
          homepage: userData.homepage,
          twitterHandle: userData.twitterHandle,
          // Application fields
          applicationSubmitted: userData.applicationSubmitted,
          applicationSubmittedAt: userData.applicationSubmittedAt?.toDate(),
          applicationBio: userData.applicationBio,
          applicationInterests: userData.applicationInterests,
          applicationPortfolio: userData.applicationPortfolio,
          applicationStatus: userData.applicationStatus,
          applicationRejectionReason: userData.applicationRejectionReason,
          canReapply: userData.canReapply,
          applicationCount: userData.applicationCount,
          // Tag permissions
          assignedTags: userData.assignedTags || [],
          requestedTags: userData.requestedTags || [],
          tagRequestedAt: userData.tagRequestedAt || {},
          // Twitter OAuth fields
          authProvider: userData.authProvider,
          twitterId: userData.twitterId,
          twitterUsername: userData.twitterUsername,
          twitterDisplayName: userData.twitterDisplayName,
          twitterProfileImageUrl: userData.twitterProfileImageUrl,
          twitterUsernameLastUpdatedAt: userData.twitterUsernameLastUpdatedAt?.toDate(),
          isGuest: userData.isGuest || false,
          // Per-user permission grants
          grantedPermissions: userData.grantedPermissions || [],
          // Use denormalized stats from user document (performance optimization)
          articleCount: userData.articleCount || 0,
          totalUploads: userData.totalUploads || 0,
          lastActivityAt: userData.lastActivityAt?.toDate(),
        };

        // Apply search filter if provided
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          const searchable = `${user.displayName} ${user.email} ${user.bio || ''}`.toLowerCase();
          if (!searchable.includes(searchTerm)) {
            continue;
          }
        }

        users.push(user);
      }

      return users;
    } catch (error) {
      throw handleUserError(error, 'Failed to retrieve users');
    }
  }

  /**
   * Get writers and editors (for editors - hides admin details)
   */
  async getWritersAndEditors(
    filters: {
      isActive?: boolean;
      search?: string;
    } = {},
    currentUser: User
  ): Promise<UserWithStats[]> {
    // Check permissions - editors can read profiles
    await RBACService.authorize(
      currentUser,
      Permission.USER_READ_PROFILES,
      undefined,
      'You do not have permission to view user profiles'
    );

    try {
      const database = this.getDb();
      let q = collection(database, this.collectionName);
      const queryConstraints: QueryConstraint[] = [];

      // NOTE: We filter isDeleted client-side to avoid index requirements

      // Show writers and editors - hide admins
      queryConstraints.push(where('role', 'in', ['writer', 'editor']));

      if (filters.isActive !== undefined) {
        queryConstraints.push(where('isActive', '==', filters.isActive));
      }

      // Add ordering by creation date
      queryConstraints.push(orderBy('createdAt', 'desc'));

      const usersQuery = query(q, ...queryConstraints);
      const snapshot = await getDocs(usersQuery);

      const users: UserWithStats[] = [];

      for (const userDoc of snapshot.docs) {
        const userData = userDoc.data();

        // Client-side filter: Skip deleted users
        if (userData.isDeleted === true) {
          continue;
        }

        // SECURITY: Editors only get limited profile info (no sensitive data)
        // Admins/Owners get full data via getAllUsers
        const user: UserWithStats = {
          id: userDoc.id,
          // NO email - sensitive
          email: '', // Hidden from editors
          displayName: userData.displayName,
          role: userData.role,
          isActive: userData.isActive,
          createdAt: userData.createdAt?.toDate() || new Date(),
          lastLoginAt: userData.lastLoginAt?.toDate(),
          profileImage: userData.profileImage,
          bio: userData.bio,
          homepage: userData.homepage,
          twitterHandle: userData.twitterHandle, // Public handle is OK
          // Twitter OAuth fields - HIDE sensitive IDs
          authProvider: userData.authProvider,
          // twitterId: HIDDEN - sensitive internal ID
          twitterUsername: userData.twitterUsername, // Public username is OK
          twitterDisplayName: userData.twitterDisplayName,
          twitterProfileImageUrl: userData.twitterProfileImageUrl,
          twitterUsernameLastUpdatedAt: userData.twitterUsernameLastUpdatedAt?.toDate(),
          // Email verification - only show verified status, not details
          emailVerified: userData.emailVerified || false,
          // Application fields - HIDDEN (sensitive)
          // applicationBio, applicationPortfolio, etc. not included
          // Tag permissions - OK for editors to see
          assignedTags: userData.assignedTags || [],
          requestedTags: userData.requestedTags || [],
          tagRequestedAt: userData.tagRequestedAt || {},
          isGuest: userData.isGuest || false,
          // Permission grants - HIDDEN (security)
          // grantedPermissions: not included for editors
          // Stats are OK
          articleCount: userData.articleCount || 0,
          totalUploads: userData.totalUploads || 0,
          lastActivityAt: userData.lastActivityAt?.toDate(),
        };

        // Apply search filter (search by display name only for editors - no email search)
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          const matchesSearch = user.displayName.toLowerCase().includes(searchTerm);

          if (!matchesSearch) {
            continue;
          }
        }

        users.push(user);
      }

      return users;
    } catch (error) {
      throw handleUserError(error, 'Failed to retrieve writers and editors');
    }
  }

  /**
   * Create a guest user (no Firebase Auth - for anonymous/pen name authors)
   * Only owners can create guest users
   */
  async createGuestUser(userData: CreateGuestUserData, currentUser: User): Promise<string> {
    // Check permissions - only owners can create guest users
    if (currentUser.role !== 'owner') {
      throw new Error('Only owners can create guest users');
    }

    try {
      const database = this.getDb();

      // Create guest user document in users collection
      // Only include fields that have values (no undefined fields for Firestore)
      const guestUser: Record<string, unknown> = {
        displayName: userData.displayName,
        role: 'writer', // Guest users are always writers
        isActive: userData.isActive !== undefined ? userData.isActive : true,
        isGuest: true,
        createdAt: serverTimestamp(),
        // Initialize denormalized stats
        articleCount: 0,
        totalUploads: 0,
        // No email, no Firebase Auth, no email verification
      };

      // Only add optional fields if they have values
      if (userData.bio) guestUser.bio = userData.bio;
      if (userData.homepage) guestUser.homepage = userData.homepage;
      if (userData.twitterHandle) guestUser.twitterHandle = userData.twitterHandle;
      if (userData.profileImage) guestUser.profileImage = userData.profileImage;

      const docRef = await addDoc(collection(database, this.collectionName), guestUser as Partial<User>);

      return docRef.id;
    } catch (error) {
      throw handleUserError(error, 'Failed to create guest user');
    }
  }

  /**
   * Create a new user with Firebase Auth and Firestore profile (with RBAC security)
   */
  async createUser(userData: CreateUserData, currentUser: User, adminCredentials?: { email: string; password: string }): Promise<string> {
    // Check permissions
    await RBACService.authorize(
      currentUser,
      Permission.USER_CREATE,
      undefined,
      'Only administrators can create new users'
    );

    if (!adminCredentials) {
      throw new Error('Admin credentials are required to maintain session during user creation');
    }

    let firebaseUser: FirebaseUser | null = null;
    const firebaseAuth = this.getAuth();
    const currentFirebaseUser = firebaseAuth.currentUser;
    const adminEmail = currentFirebaseUser?.email;

    try {
      // AH-2 allowlist: never mint an out-of-enum role.
      assertAssignableRole(userData.role);
      // AH-6 owner-mints-owner: only an owner may create an owner.
      assertOwnerMintAuthority(userData.role, currentUser);

      // For email/password users, email and password are required
      if (!userData.email || !userData.password) {
        throw new Error('Email and password are required for email/password authentication');
      }

      // Create Firebase Auth user (this will sign them in automatically)
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth,
        userData.email,
        userData.password
      );
      firebaseUser = userCredential.user;

      // Send email verification to the new user
      await sendEmailVerification(firebaseUser);

      // Create Firestore user document with email verification pending
      const database = this.getDb();
      const newUser: Omit<User, 'id'> = {
        email: userData.email,
        displayName: userData.displayName,
        role: userData.role,
        isActive: false, // Keep inactive until email is verified
        createdAt: new Date(),
      };

      const userDocRef = doc(database, this.collectionName, firebaseUser.uid);
      await setDoc(userDocRef, {
        ...newUser,
        createdAt: serverTimestamp(),
        emailVerified: false,
        pendingEmailVerification: true,
        // Initialize denormalized stats
        articleCount: 0,
        totalUploads: 0,
      });

      // CRITICAL: Sign the admin back in to restore their session
      await signInWithEmailAndPassword(firebaseAuth, adminCredentials.email, adminCredentials.password);

      // Log activity (non-critical, fail silently)
      try {
        const { activityLogService } = await import('./activity-log-service');
        await activityLogService.logUserCreated(
          currentUser,
          firebaseUser.uid,
          userData.displayName,
          userData.role
        );
      } catch (logError) {
        handleSilentError(logError, 'UserManagementService.createUser - Activity logging');
      }

      return firebaseUser.uid;
    } catch (error) {
      // If Firestore creation failed but auth user was created, clean up the auth user
      if (firebaseUser) {
        try {
          await deleteUser(firebaseUser);
        } catch (cleanupError) {
          handleSilentError(cleanupError, 'UserManagementService.createUser - Auth cleanup');
        }
      }

      // Try to restore admin session if it was lost
      if (adminEmail && firebaseAuth.currentUser?.email !== adminEmail) {
        try {
          await signInWithEmailAndPassword(firebaseAuth, adminCredentials.email, adminCredentials.password);
        } catch (signInError) {
          handleSilentError(signInError, 'UserManagementService.createUser - Restore admin session');
        }
      }

      throw handleUserError(error, 'Failed to create user');
    }
  }

  /**
   * Update an existing user (with RBAC security)
   */
  async updateUser(
    userId: string,
    userData: UpdateUserData,
    currentUser: User
  ): Promise<void> {
    // Check permissions based on whether updating own profile or others
    const isOwnProfile = currentUser.id === userId;

    if (isOwnProfile) {
      // Users can update their own profile
      await RBACService.authorize(
        currentUser,
        Permission.USER_EDIT_OWN,
        { ownerId: userId },
        'You can only edit your own profile'
      );
    } else {
      // Only admins can update other users
      await RBACService.authorize(
        currentUser,
        Permission.USER_EDIT_ALL,
        undefined,
        'Only administrators can edit other users'
      );
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const currentUserData = userDoc.data() as User;

      // Prevent non-admin/owner users from changing role or admin-only fields
      const updateData: Record<string, unknown> = {};

      if (currentUser.role === 'admin' || currentUser.role === 'owner') {
        // Admins and owners can update everything
        if (userData.email !== undefined) updateData.email = userData.email;
        if (userData.displayName !== undefined) updateData.displayName = userData.displayName;
        if (userData.role !== undefined) {
          assertAssignableRole(userData.role); // AH-2 allowlist: reject out-of-enum roles
          // AH-6: only owners may MINT owner — pass the prior role so editing/demoting an
          // existing owner is allowed (mint = promoting a non-owner target to owner).
          assertOwnerMintAuthority(userData.role, currentUser, currentUserData.role);
          updateData.role = userData.role;
        }
        if (userData.isActive !== undefined) updateData.isActive = userData.isActive;
        if (userData.bio !== undefined) updateData.bio = userData.bio;
        if (userData.homepage !== undefined) updateData.homepage = userData.homepage;
        if (userData.twitterHandle !== undefined) updateData.twitterHandle = userData.twitterHandle;
        if (userData.twitterUsername !== undefined) updateData.twitterUsername = userData.twitterUsername;
        if (userData.twitterId !== undefined) updateData.twitterId = userData.twitterId;
        if (userData.twitterDisplayName !== undefined) updateData.twitterDisplayName = userData.twitterDisplayName;
        if (userData.twitterProfileImageUrl !== undefined) updateData.twitterProfileImageUrl = userData.twitterProfileImageUrl;
        if (userData.authProvider !== undefined) updateData.authProvider = userData.authProvider;
        if (userData.profileImage !== undefined) updateData.profileImage = userData.profileImage;
        
        // Only owners can grant permissions (sensitive operation)
        if (currentUser.role === 'owner' && userData.grantedPermissions !== undefined) {
          updateData.grantedPermissions = userData.grantedPermissions;
        }
      } else {
        // Regular users can only update their profile information (not verified Twitter fields)
        if (userData.displayName !== undefined) updateData.displayName = userData.displayName;
        if (userData.bio !== undefined) updateData.bio = userData.bio;
        if (userData.homepage !== undefined) updateData.homepage = userData.homepage;
        if (userData.twitterHandle !== undefined) updateData.twitterHandle = userData.twitterHandle; // DEPRECATED
        if (userData.profileImage !== undefined) updateData.profileImage = userData.profileImage;
        // Note: Regular users cannot update verified Twitter fields (twitterId, twitterUsername, etc.)
      }

      updateData.updatedAt = serverTimestamp();

      await updateDoc(userDocRef, updateData as Partial<User>);

      // Log activity for important changes (non-critical, fail silently)
      try {
        const { activityLogService } = await import('./activity-log-service');

        // Log role changes
        if (userData.role !== undefined && currentUserData.role !== userData.role) {
          await activityLogService.logActivity(
            'user_role_changed',
            currentUser,
            'user',
            userId,
            currentUserData.displayName,
            { oldRole: currentUserData.role, newRole: userData.role },
            'owner_only'
          );
        }

        // Log activation/deactivation
        if (userData.isActive !== undefined && currentUserData.isActive !== userData.isActive) {
          await activityLogService.logUserStatusChange(
            currentUser,
            userId,
            currentUserData.displayName,
            userData.isActive
          );
        }

        // Log permission grant changes
        if (userData.grantedPermissions !== undefined) {
          const oldGrants = currentUserData.grantedPermissions || [];
          const newGrants = userData.grantedPermissions || [];
          const added = newGrants.filter(p => !oldGrants.includes(p));
          const removed = oldGrants.filter(p => !newGrants.includes(p));
          
          if (added.length > 0 || removed.length > 0) {
            await activityLogService.logActivity(
              'user_permissions_changed',
              currentUser,
              'user',
              userId,
              currentUserData.displayName,
              { 
                added: added.length > 0 ? added : undefined, 
                removed: removed.length > 0 ? removed : undefined 
              },
              'owner_only'
            );
          }
        }
      } catch (logError) {
        handleSilentError(logError, 'UserManagementService.updateUser - Activity logging');
      }

      // Trigger revalidation for author profile updates (non-critical, fail silently)
      try {
        await revalidationService.revalidateAuthor(userId);
      } catch (revalidationError) {
        handleSilentError(revalidationError, 'UserManagementService.updateUser - Revalidation');
      }
    } catch (error) {
      throw handleUserError(error, 'Failed to update user');
    }
  }

  /**
   * Soft delete a user (move to trash) (admin only with RBAC security)
   */
  async deleteUser(userId: string, currentUser: User, deletionReason?: string): Promise<void> {
    // Check permissions
    await RBACService.authorize(
      currentUser,
      Permission.USER_DELETE,
      undefined,
      'Only administrators can delete users'
    );

    try {
      const database = this.getDb();

      // Get user data for logging
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data() as User;

      // Check if user has any published articles
      const articlesQuery = query(
        collection(database, 'articles'),
        where('author', '==', userId),
        where('status', '==', 'published'),
        where('isDeleted', '==', false)
      );
      const articlesSnapshot = await getDocs(articlesQuery);

      if (!articlesSnapshot.empty) {
        throw new Error('Cannot delete user with published articles. Please reassign or unpublish articles first.');
      }

      // Soft delete: mark as deleted instead of removing
      await updateDoc(userDocRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: currentUser.id,
        deletionReason: deletionReason || null,
        isActive: false, // Also deactivate when deleting
      });

      // Log activity (non-critical, fail silently)
      try {
        const { activityLogService } = await import('./activity-log-service');
        await activityLogService.logActivity(
          'user_deleted',
          currentUser,
          'user',
          userId,
          userData.displayName,
          {
            reason: deletionReason,
            userEmail: userData.email,
            userRole: userData.role,
            daysUntilPermanent: 90
          },
          'owner_only'
        );
      } catch (logError) {
        handleSilentError(logError, 'UserManagementService.deleteUser - Activity logging');
      }

      // Note: Firebase Auth user deletion should be handled separately
      // as it requires special admin SDK permissions
    } catch (error) {
      throw handleUserError(error, 'Failed to delete user');
    }
  }

  /**
   * Get trashed users (owner only)
   */
  async getTrashedUsers(currentUser: User): Promise<User[]> {
    // Check permissions - only owners can view trashed users
    if (currentUser.role !== 'owner') {
      throw new Error('Only owners can view trashed users');
    }

    try {
      const database = this.getDb();
      const q = query(
        collection(database, this.collectionName),
        where('isDeleted', '==', true),
        orderBy('deletedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const users: User[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
          applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
          emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
          lastActivityAt: data.lastActivityAt?.toDate(),
          deletedAt: data.deletedAt?.toDate(),
        } as User);
      });

      return users;
    } catch (error) {
      throw handleUserError(error, 'Failed to retrieve trashed users');
    }
  }

  /**
   * Restore user from trash (owner only)
   */
  async restoreUser(userId: string, currentUser: User): Promise<void> {
    // Check permissions - only owners can restore users
    if (currentUser.role !== 'owner') {
      throw new Error('Only owners can restore users');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      if (!userData.isDeleted) {
        throw new Error('User is not in trash');
      }

      // Restore: remove deletion fields, but keep isActive as false
      // Owner must manually activate user after restoration
      await updateDoc(userDocRef, {
        isDeleted: false,
        deletedAt: null,
        deletedBy: null,
        deletionReason: null,
        // isActive remains as it was (likely false)
      });

      // Log activity (non-critical, fail silently)
      try {
        const { activityLogService } = await import('./activity-log-service');
        await activityLogService.logActivity(
          'user_restored',
          currentUser,
          'user',
          userId,
          userData.displayName,
          { restoredFrom: 'trash', userEmail: userData.email },
          'owner_only'
        );
      } catch (logError) {
        handleSilentError(logError, 'UserManagementService.restoreUser - Activity logging');
      }


    } catch (error) {
      throw handleUserError(error, 'Failed to restore user');
    }
  }

  /**
   * Permanently delete user (owner only)
   */
  async permanentlyDeleteUser(userId: string, currentUser: User): Promise<void> {
    // Check permissions - only owners can permanently delete
    if (currentUser.role !== 'owner') {
      throw new Error('Only owners can permanently delete users');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();

      // Safety check: user should be in trash
      if (!userData.isDeleted) {
        throw new Error('User must be in trash before permanent deletion. Move to trash first.');
      }

      // Check again for published articles (extra safety)
      const articlesQuery = query(
        collection(database, 'articles'),
        where('author', '==', userId),
        where('status', '==', 'published'),
        where('isDeleted', '==', false)
      );
      const articlesSnapshot = await getDocs(articlesQuery);

      if (!articlesSnapshot.empty) {
        throw new Error('Cannot permanently delete user with published articles.');
      }

      // Actually delete from Firestore
      await deleteDoc(userDocRef);

      console.log(`🗑️ Permanently deleted user ${userId}: ${userData.displayName}`);

      // Note: Firebase Auth user deletion should be handled separately
    } catch (error) {
      throw handleUserError(error, 'Failed to permanently delete user');
    }
  }

  /**
   * Auto-cleanup: Delete users trashed >90 days ago
   */
  async cleanupOldTrashedUsers(): Promise<number> {
    try {
      const database = this.getDb();

      // Calculate 90 days ago
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const q = query(
        collection(database, this.collectionName),
        where('isDeleted', '==', true),
        where('deletedAt', '<', Timestamp.fromDate(ninetyDaysAgo))
      );

      const querySnapshot = await getDocs(q);
      let deletedCount = 0;

      for (const docSnapshot of querySnapshot.docs) {
        try {
          // Verify no published articles before permanent deletion
          const articlesQuery = query(
            collection(database, 'articles'),
            where('author', '==', docSnapshot.id),
            where('status', '==', 'published'),
            where('isDeleted', '==', false)
          );
          const articlesSnapshot = await getDocs(articlesQuery);

          if (articlesSnapshot.empty) {
            await deleteDoc(doc(database, this.collectionName, docSnapshot.id));
            deletedCount++;
          } else {
            console.log(`⚠️ Skipping user ${docSnapshot.id} - has published articles`);
          }
        } catch (deleteError) {
          handleSilentError(deleteError, `UserManagementService.cleanupOldTrashedUsers - Delete user ${docSnapshot.id}`);
        }
      }

      console.log(`🗑️ Cleaned up ${deletedCount} old trashed users`);
      return deletedCount;
    } catch (error) {
      handleSilentError(error, 'UserManagementService.cleanupOldTrashedUsers');
      return 0;
    }
  }

  /**
   * Increment article count for a user (called when article is created)
   * Non-critical - fails silently to avoid blocking article creation
   */
  async incrementArticleCount(userId: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      await updateDoc(userDocRef, {
        articleCount: increment(1),
        lastActivityAt: serverTimestamp(),
      });
    } catch (error) {
      handleSilentError(error, `UserManagementService.incrementArticleCount - User ${userId}`);
    }
  }

  /**
   * Decrement article count for a user (called when article is deleted)
   * Non-critical - fails silently to avoid blocking article deletion
   */
  async decrementArticleCount(userId: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);

      // Get current count to prevent going negative
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentCount = userDoc.data().articleCount || 0;
        if (currentCount <= 0) {
          console.warn(`⚠️ User ${userId} already has articleCount of ${currentCount}, skipping decrement`);
          return; // Don't decrement if already at 0 or negative
        }
      }

      await updateDoc(userDocRef, {
        articleCount: increment(-1),
        lastActivityAt: serverTimestamp(),
      });
    } catch (error) {
      handleSilentError(error, `UserManagementService.decrementArticleCount - User ${userId}`);
    }
  }


  /**
   * Increment upload count for a user (called when media is uploaded)
   * Non-critical - fails silently to avoid blocking media upload
   */
  async incrementUploadCount(userId: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      await updateDoc(userDocRef, {
        totalUploads: increment(1),
        lastActivityAt: serverTimestamp(),
      });
    } catch (error) {
      handleSilentError(error, `UserManagementService.incrementUploadCount - User ${userId}`);
    }
  }

  /**
   * Decrement upload count for a user (called when media is deleted)
   * Non-critical - fails silently to avoid blocking media deletion
   */
  async decrementUploadCount(userId: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);

      // Get current count to prevent going negative
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentCount = userDoc.data().totalUploads || 0;
        if (currentCount <= 0) {
          console.warn(`⚠️ User ${userId} already has totalUploads of ${currentCount}, skipping decrement`);
          return; // Don't decrement if already at 0 or negative
        }
      }

      await updateDoc(userDocRef, {
        totalUploads: increment(-1),
        lastActivityAt: serverTimestamp(),
      });
    } catch (error) {
      handleSilentError(error, `UserManagementService.decrementUploadCount - User ${userId}`);
    }
  }

  /**
   * Check if user has permission to perform an action
   */
  hasPermission(
    userRole: string,
    action: 'create_user' | 'edit_user' | 'delete_user' | 'view_users' | 'edit_roles'
  ): boolean {
    switch (action) {
      case 'create_user':
      case 'delete_user':
      case 'edit_roles':
        return userRole === 'admin';
      case 'edit_user':
      case 'view_users':
        return userRole === 'admin' || userRole === 'editor' || userRole === 'writer';
      default:
        return false;
    }
  }

  /**
   * Check and update email verification status for a user
   */
  async updateEmailVerificationStatus(userId: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      // Get current Firebase Auth user verification status
      // Note: This requires the user to be signed in, so this method
      // should be called when users sign in to check their verification status
      const userData = userDoc.data();

      // If user was pending verification and now should be activated
      if (userData.pendingEmailVerification && userData.emailVerified) {
        await updateDoc(userDocRef, {
          isActive: true,
          pendingEmailVerification: false,
          emailVerificationCompletedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      throw handleUserError(error, 'Failed to update email verification status');
    }
  }

  /**
   * Manually activate a user (admin/owner only) - useful for fixing verification issues
   */
  async manuallyActivateUser(userId: string, currentUserRole: string): Promise<void> {
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') {
      throw new Error('Only administrators and owners can manually activate users');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      await updateDoc(userDocRef, {
        isActive: true,
        emailVerified: true,
        pendingEmailVerification: false,
        emailVerificationCompletedAt: serverTimestamp(),
        manuallyActivatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw handleUserError(error, 'Failed to manually activate user');
    }
  }

  /**
   * Get multiple users by IDs in batch (performance optimization)
   * 
   * Uses Firestore `in` queries with chunking (30 items max per query)
   * and LRU-style caching for improved performance.
   * 
   * Phase 1 Optimization: Reduced from N individual getDoc calls to
   * ceil(N/30) batched queries + cache hits.
   */
  async getBatchUsers(userIds: string[]): Promise<Map<string, User>> {
    const userMap = new Map<string, User>();

    if (userIds.length === 0) {
      return userMap;
    }

    try {
      const database = this.getDb();
      if (!database) return userMap;

      const uniqueIds = [...new Set(userIds)];
      const now = Date.now();
      const uncachedIds: string[] = [];

      // Check cache first
      for (const userId of uniqueIds) {
        const cached = this.userCache.get(userId);
        if (cached && (now - cached.timestamp) < this.USER_CACHE_DURATION) {
          userMap.set(userId, cached.user);
        } else {
          uncachedIds.push(userId);
        }
      }

      // Fetch uncached users using batched `in` queries
      if (uncachedIds.length > 0) {
        const chunks = chunkArray(uncachedIds, FIRESTORE_IN_QUERY_LIMIT);
        const usersCollection = collection(database, this.collectionName);

        const chunkPromises = chunks.map(async (chunkIds) => {
          try {
            const q = query(usersCollection, where(documentId(), 'in', chunkIds));
            const snapshot = await getDocs(q);
            return snapshot.docs;
          } catch (error) {
            handleSilentError(error, `UserManagementService.getBatchUsers - chunk of ${chunkIds.length}`);
            return [];
          }
        });

        const chunkResults = await Promise.all(chunkPromises);

        for (const docs of chunkResults) {
          for (const userDoc of docs) {
            const data = userDoc.data();
            const user: User = {
              id: userDoc.id,
              email: data.email || '',
              displayName: data.displayName || data.email?.split('@')[0] || userDoc.id,
              role: data.role || 'viewer',
              isActive: data.isActive !== false,
              isGuest: data.isGuest || false,
              createdAt: data.createdAt?.toDate() || new Date(),
              profileImage: data.profilePicture || data.profileImage,
              bio: data.bio,
              lastLoginAt: data.lastLoginAt?.toDate(),
              twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
              applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
              emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
              lastActivityAt: data.lastActivityAt?.toDate(),
            };

            // Update cache
            this.userCache.set(userDoc.id, { user, timestamp: now });
            userMap.set(userDoc.id, user);
          }
        }

        // Cleanup cache if needed
        this.cleanupCache();
      }

      return userMap;
    } catch (error) {
      handleSilentError(error, 'UserManagementService.getBatchUsers');
      return userMap;
    }
  }

  /**
   * Get user by ID
   * Returns null if not found (graceful degradation)
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const database = this.getDb();
      const userDoc = await getDoc(doc(database, this.collectionName, userId));

      if (!userDoc.exists()) {
        return null;
      }

      const data = userDoc.data();

      return {
        id: userDoc.id,
        email: data.email,
        displayName: data.displayName,
        role: data.role,
        isActive: data.isActive,
        isGuest: data.isGuest || false,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate(),
        twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
        applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
        emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
        lastActivityAt: data.lastActivityAt?.toDate(),
        profileImage: data.profileImage,
        bio: data.bio,
        homepage: data.homepage,
        twitterHandle: data.twitterHandle,
      };
    } catch (error) {
      return handleDataFetchError(error, 'UserManagementService.getUserById', userId);
    }
  }

  /**
   * Get user by display name slug (for public author pages)
   * Returns null if not found (graceful degradation)
   */
  async getUserByDisplayNameSlug(slug: string): Promise<User | null> {
    try {
      const database = this.getDb();

      // Get all active users and find the one with matching slug
      const q = query(
        collection(database, this.collectionName),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);

      for (const userDoc of querySnapshot.docs) {
        const data = userDoc.data();
        // Generate slug from displayName and compare
        const userSlug = generateAuthorSlug(data.displayName);

        if (userSlug === slug) {
          return {
            id: userDoc.id,
            email: data.email,
            displayName: data.displayName,
            role: data.role,
            isActive: data.isActive,
            createdAt: data.createdAt?.toDate() || new Date(),
            lastLoginAt: data.lastLoginAt?.toDate(),
            twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
            applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
            emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
            lastActivityAt: data.lastActivityAt?.toDate(),
            profileImage: data.profileImage,
            bio: data.bio,
            homepage: data.homepage,
            twitterHandle: data.twitterHandle,
          };
        }
      }

      return null;
    } catch (error) {
      return handleDataFetchError(error, 'UserManagementService.getUserByDisplayNameSlug', slug);
    }
  }

  /**
   * Get all active staff members (writers, editors, admins) for public display
   * This method is designed to work without admin permissions
   */
  async getActiveStaffMembers(): Promise<User[]> {
    try {
      const database = this.getDb();

      // Get all active users (this should work with public read rules)
      // NOTE: We filter isDeleted client-side to avoid index requirements
      const q = query(
        collection(database, this.collectionName),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      const users: User[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Client-side filter: Skip deleted users
        if (data.isDeleted === true) {
          return;
        }
        // Only include users with content creation roles
        if (['writer', 'editor', 'admin', 'owner'].includes(data.role)) {
          users.push({
            id: doc.id,
            email: data.email,
            displayName: data.displayName,
            role: data.role,
            isActive: data.isActive,
            isGuest: data.isGuest || false,
            createdAt: data.createdAt?.toDate() || new Date(),
            lastLoginAt: data.lastLoginAt?.toDate(),
            twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
            applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
            emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
            lastActivityAt: data.lastActivityAt?.toDate(),
            profileImage: data.profileImage,
            bio: data.bio,
            homepage: data.homepage,
            twitterHandle: data.twitterHandle,
          });
        }
      });

      return users;
    } catch (error) {
      handleSilentError(error, 'UserManagementService.getActiveStaffMembers');
      return [];
    }
  }
}

// Export singleton instance
export const userManagementService = UserManagementService.getInstance();

